var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, CardActions, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, Snackbar, } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { businessService } from '@services/businessService';
import BusinessModal from './BusinessModal';
const Businesses = () => {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [businessToDelete, setBusinessToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    useEffect(() => {
        loadBusinesses();
    }, []);
    const loadBusinesses = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            const data = yield businessService.getAll();
            setBusinesses(data);
        }
        catch (error) {
            console.error('Error loading businesses:', error);
            showSnackbar('Error loading businesses', 'error');
        }
        finally {
            setLoading(false);
        }
    });
    const handleCreateNew = () => {
        setSelectedBusiness(null);
        setModalOpen(true);
    };
    const handleCardDoubleClick = (business) => {
        setSelectedBusiness(business);
        setModalOpen(true);
    };
    const handleEdit = (business, event) => {
        event.stopPropagation();
        setSelectedBusiness(business);
        setModalOpen(true);
    };
    const handleDeleteClick = (business, event) => {
        event.stopPropagation();
        setBusinessToDelete(business);
        setDeleteDialogOpen(true);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!businessToDelete)
            return;
        try {
            yield businessService.delete(businessToDelete.id);
            showSnackbar('Business deleted successfully', 'success');
            loadBusinesses();
        }
        catch (error) {
            console.error('Error deleting business:', error);
            showSnackbar('Error deleting business', 'error');
        }
        finally {
            setDeleteDialogOpen(false);
            setBusinessToDelete(null);
        }
    });
    const handleSave = (businessData) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (selectedBusiness) {
                yield businessService.update(selectedBusiness.id, businessData);
                showSnackbar('Business updated successfully', 'success');
            }
            else {
                yield businessService.create(businessData);
                showSnackbar('Business created successfully', 'success');
            }
            loadBusinesses();
        }
        catch (error) {
            console.error('Error saving business:', error);
            showSnackbar('Error saving business', 'error');
            throw error;
        }
    });
    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };
    const handleCloseSnackbar = () => {
        setSnackbar(Object.assign(Object.assign({}, snackbar), { open: false }));
    };
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "h5", sx: { textAlign: { xs: 'center', md: 'left' } }, children: "Businesses" }), _jsx(Box, { sx: {
                    display: 'flex',
                    justifyContent: { xs: 'flex-end', md: 'space-between' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3
                }, children: _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: handleCreateNew, sx: {
                        width: { xs: '100%', md: 'auto' },
                        fontSize: { xs: '0.8rem', md: '0.875rem' }
                    }, children: "Create New Business" }) }), loading ? (_jsx(Typography, { children: "Loading businesses..." })) : businesses.length === 0 ? (_jsxs(Box, { sx: { textAlign: 'center', py: 4 }, children: [_jsx(Typography, { variant: "h6", color: "text.secondary", gutterBottom: true, children: "No businesses found" }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: "Create your first business to get started" }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: handleCreateNew, children: "Create Business" })] })) : (_jsx(Grid, { container: true, spacing: 3, children: businesses.map((business) => (_jsx(Grid, { item: true, xs: 12, sm: 6, md: 4, children: _jsxs(Card, { sx: {
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 4,
                            },
                        }, onDoubleClick: () => handleCardDoubleClick(business), children: [_jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: business.name }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { minHeight: 60 }, children: business.description || 'No description' }), _jsxs(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mt: 2 }, children: ["Created: ", new Date(business.createdAt).toLocaleDateString()] })] }), _jsxs(CardActions, { sx: { justifyContent: 'flex-end' }, children: [_jsx(IconButton, { size: "small", color: "primary", onClick: (e) => handleEdit(business, e), title: "Edit", children: _jsx(EditIcon, {}) }), _jsx(IconButton, { size: "small", color: "error", onClick: (e) => handleDeleteClick(business, e), title: "Delete", children: _jsx(DeleteIcon, {}) })] })] }) }, business.id))) })), _jsx(BusinessModal, { open: modalOpen, business: selectedBusiness, onClose: () => setModalOpen(false), onSave: handleSave }), _jsxs(Dialog, { open: deleteDialogOpen, onClose: () => setDeleteDialogOpen(false), children: [_jsx(DialogTitle, { children: "Confirm Delete" }), _jsx(DialogContent, { children: _jsxs(DialogContentText, { children: ["Are you sure you want to delete \"", businessToDelete === null || businessToDelete === void 0 ? void 0 : businessToDelete.name, "\"? This action cannot be undone."] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setDeleteDialogOpen(false), children: "Cancel" }), _jsx(Button, { onClick: handleDeleteConfirm, color: "error", variant: "contained", children: "Delete" })] })] }), _jsx(Snackbar, { open: snackbar.open, autoHideDuration: 6000, onClose: handleCloseSnackbar, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, children: _jsx(Alert, { onClose: handleCloseSnackbar, severity: snackbar.severity, sx: { width: '100%' }, children: snackbar.message }) })] }));
};
export default Businesses;
